import api from '../axios-instance';

import type { ApiResponse } from './api.types';
import type { CoverageViewModel } from '../types/CoverageViewModel';
import type { MovePolicyResponse } from '../types/MovePolicyResponse';
import type { CoverageTimelineViewModel } from '../types/CoverageTimelineViewModel';

type BatchMoveCoverageResponse = {
  destinationComboId: number;
  movedCoverageIds: number[];
  primaryCoverageId: number;
};

function toThrownError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (
    typeof error === 'object'
    && error !== null
    && 'message' in error
    && typeof (error as { message: unknown }).message === 'string'
  ) {
    return new Error((error as { message: string }).message);
  }

  return new Error('Unexpected non-Error thrown');
}

export const coverageService = {
  getCoverageTimelineById: async (
    comboId: number,
    coverageId: number
  ): Promise<ApiResponse<CoverageTimelineViewModel>> => {
    try {
      const response = await api.get(`/combos/${comboId}/coverages/${coverageId}/timeline`);
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  getCoverageById: async (
    comboId: number,
    coverageId: number
  ): Promise<ApiResponse<CoverageViewModel>> => {
    try {
      const response = await api.get(`/combos/${comboId}/coverages/${coverageId}`);
      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  moveCoverageToExistingCombo: async (params: {
    comboId: number;
    coverageId: number;
    targetComboId: number;
    note: string;
  }): Promise<ApiResponse<MovePolicyResponse>> => {
    try {
      const response = await api.post(
        `/combos/${params.comboId}/coverages/${params.coverageId}/move-existing-combo`,
        {
          targetComboId: params.targetComboId,
          note: params.note,
        }
      );

      if (!response.data?.newComboId || !response.data?.newCoverageId) {
        throw new Error('Move succeeded but the API returned an unexpected null value.');
      }

      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  moveCoverageToNewCombo: async (params: {
    comboId: number;
    coverageId: number;
    note: string;
  }): Promise<ApiResponse<MovePolicyResponse>> => {
    try {
      const response = await api.post(
        `/combos/${params.comboId}/coverages/${params.coverageId}/move-new-combo`,
        {
          note: params.note,
        }
      );

      if (!response.data?.newComboId || !response.data?.newCoverageId) {
        throw new Error('Move succeeded but the API returned an unexpected null value.');
      }

      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  moveCoveragesToExistingComboBatch: async (params: {
    comboId: number;
    coverageIds: number[];
    targetComboId: number;
    note: string;
  }): Promise<ApiResponse<BatchMoveCoverageResponse>> => {
    try {
      const response = await api.post(
        `/combos/${params.comboId}/coverages/move-existing-combo-batch`,
        {
          coverageIds: params.coverageIds,
          targetComboId: params.targetComboId,
          note: params.note,
        }
      );

      if (
        !response.data?.destinationComboId
        || !response.data?.primaryCoverageId
        || !Array.isArray(response.data?.movedCoverageIds)
        || response.data.movedCoverageIds.length === 0
      ) {
        throw new Error('Move succeeded but the API returned an unexpected null value.');
      }

      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },

  moveCoveragesToNewComboBatch: async (params: {
    comboId: number;
    coverageIds: number[];
    note: string;
  }): Promise<ApiResponse<BatchMoveCoverageResponse>> => {
    try {
      const response = await api.post(
        `/combos/${params.comboId}/coverages/move-new-combo-batch`,
        {
          coverageIds: params.coverageIds,
          note: params.note,
        }
      );

      if (
        !response.data?.destinationComboId
        || !response.data?.primaryCoverageId
        || !Array.isArray(response.data?.movedCoverageIds)
        || response.data.movedCoverageIds.length === 0
      ) {
        throw new Error('Move succeeded but the API returned an unexpected null value.');
      }

      return { data: response.data, status: response.status };
    } catch (error) {
      throw toThrownError(error);
    }
  },
};
